import React from 'react';
import {
  ChakraAutocompleteField,
  ChakraFormSwitch,
  InputField,
} from 'app/components/Core';
import { Controller } from 'react-hook-form';
import { SongGenre } from 'app/components/Song/Details/SongDetailsForm/SongGenre';
import { getOptions } from 'app/components/Song/Details/SongDetailsForm/utils';
import { ContentContainerType } from 'types/enums';
import { DurationTextField } from 'app/components/Song/Details/SongDetailsForm/DurationTextField';
import {
  getAutocompleteFallback,
  getFieldFallback,
} from 'app/components/Releases/LabelVersionDetailsForm/utils';
import { TimeSignatureInput } from 'app/components/Song/Details/TimeSignatureInput';
import { ContentContainer } from 'app/components/Core/ChakraContentContainer';
import { LabelVersionDetailsFormGrid } from 'app/components/Releases/LabelVersionDetailsFormGrid';
import {
  FallbackAutocompleteField,
  FallbackTextField,
  LabelVersionDetailsFormComponentProps,
} from './types';
import { useGenres, useLabelVersionForm } from './hooks';
import { ReleaseActions } from '../ReleaseActions';

export const LabelVersionDetailsFormComponent: React.FC<LabelVersionDetailsFormComponentProps> = (
  props
) => {
  const {
    releaseVersion,
    languageCodes,
    songDDL,
    validations,
    isSyncing,
  } = props;

  const { song } = releaseVersion;
  const languages = getOptions(validations.language, languageCodes);
  const keySignatures = getOptions(
    validations.keySignature,
    songDDL.keySignatures
  );
  const { genres, selectedGenres } = useGenres({ songDDL, song });
  const {
    disableAllFields,
    onSubmit,
    setValue,
    register,
    formState: { errors },
    control,
  } = useLabelVersionForm(props);

  const fallbackTextFieldToSnapshot: FallbackTextField = (name) => (event) => {
    if (event.target.value === '') {
      setValue(name, getFieldFallback(name, releaseVersion));
    }
    onSubmit();
  };

  const fallbackAutocompleteFieldToSnapshot: FallbackAutocompleteField = (
    name,
    options
  ) => (event) => {
    const { value, fallback } = getAutocompleteFallback(
      name,
      options,
      releaseVersion,
      event
    );

    if (value === '' && fallback) {
      setValue(name, fallback);
    }
    onSubmit();
  };

  return (
    <ContentContainer type={ContentContainerType.LIST} pb={10}>
      <ReleaseActions tab="details" syncState={isSyncing} />
      <form onSubmit={onSubmit}>
        <LabelVersionDetailsFormGrid
          altTitle={
            <InputField
              {...register('altTitle')}
              label="Alt. Title"
              isDisabled={disableAllFields}
              onBlur={fallbackTextFieldToSnapshot('altTitle')}
              isInvalid={Boolean(errors?.altTitle)}
              errorMessage={errors?.altTitle?.message}
            />
          }
          artist={
            <InputField
              {...register('displayArtistName')}
              label="Artist"
              isDisabled={disableAllFields}
              onBlur={fallbackTextFieldToSnapshot('displayArtistName')}
              isInvalid={Boolean(errors?.displayArtistName)}
              errorMessage={errors?.displayArtistName?.message}
            />
          }
          genres={<SongGenre genreOptions={genres} selected={selectedGenres} />}
          keySignature={
            <ChakraAutocompleteField
              control={control}
              name="keySignature"
              label="Key Signature"
              options={keySignatures}
              isClearable
              isDisabled={disableAllFields}
              onBlur={fallbackAutocompleteFieldToSnapshot(
                'keySignature',
                keySignatures
              )}
            />
          }
          timeSignature={
            <TimeSignatureInput
              control={control}
              name="timeSignature"
              label="Time Signature"
              disabled={disableAllFields}
              onChange={onSubmit}
            />
          }
          tempo={
            <InputField
              {...register('tempo')}
              label="Tempo"
              isDisabled={disableAllFields}
              onBlur={fallbackTextFieldToSnapshot('tempo')}
              isInvalid={Boolean(errors?.tempo)}
              errorMessage={errors?.tempo?.message}
            />
          }
          duration={
            <Controller
              name="duration"
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <DurationTextField
                  value={value}
                  onChange={onChange}
                  error={error?.message}
                  onBlur={fallbackTextFieldToSnapshot('duration')}
                  disabled={disableAllFields}
                />
              )}
            />
          }
          language={
            <ChakraAutocompleteField
              control={control}
              name="language"
              label="Language"
              options={languages}
              isClearable
              isDisabled={disableAllFields}
              onBlur={fallbackAutocompleteFieldToSnapshot(
                'language',
                languages
              )}
            />
          }
          explicit={
            <ChakraFormSwitch
              control={control}
              label="Explicit Lyrics"
              name="explicit"
              isDisabled={disableAllFields}
              onChange={onSubmit}
            />
          }
          location={
            <InputField
              {...register('location')}
              label="Studio Location (main)"
              isDisabled={disableAllFields}
              onBlur={fallbackTextFieldToSnapshot('location')}
              isInvalid={Boolean(errors?.location)}
              errorMessage={errors?.location?.message}
            />
          }
        />
      </form>
    </ContentContainer>
  );
};
